// Verb paradigms, ported one-to-one from tools/generate_paradigms.py (conj_a, conj_long, AS_DHATU).
// Rule numbers (नि.) refer to प्राकृत स्वयं-शिक्षक खण्ड १.

import type {
    AsDhatu,
    Cell,
    FormEntry,
    FormStatus,
    KridantaTable,
    Num,
    Person,
    PersonTable,
    VerbForms
} from '@/prakrit/lib/grammar/types'

import { addMatra, nfc } from '@/prakrit/lib/grammar/devanagari'
import { NUMBERS, PERSONS } from '@/prakrit/lib/grammar/types'

/** One generated form. Every form starts as needs_review; only a reviewer can change that. */
export const F = (form: string, status: FormStatus = 'core', rule?: string, note?: string): FormEntry => {
    const entry: FormEntry = { form: nfc(form), status, review_status: 'needs_review' }

    if (rule) entry.rule = rule
    if (note) entry.note = note

    return entry
}

type CellMap = Record<`${Person}.${Num}`, Cell>

export const table = (cells: CellMap): PersonTable => {
    const out = {} as PersonTable

    for (const p of PERSONS) {
        out[p] = {} as Record<Num, Cell>

        for (const n of NUMBERS) out[p][n] = cells[`${p}.${n}`]
    }

    return out
}

/** The same forms in all six cells (भूत, क्रियातिपत्ति). Each cell gets its own copy. */
export const allSame = (forms: Cell): PersonTable => {
    const out = {} as PersonTable

    for (const p of PERSONS) {
        out[p] = {} as Record<Num, Cell>

        for (const n of NUMBERS) out[p][n] = forms.map(f => ({ ...f }))
    }

    return out
}

/** अकारान्त धातु (पढ, पास, हस …). */
export const conjA = (s: string): VerbForms => {
    const i = addMatra(s, 'इ')   // पढि
    const e = addMatra(s, 'ए')   // पढे
    const aa = addMatra(s, 'आ')  // पढा
    const ii = addMatra(s, 'ई')  // पढी

    const out = {} as VerbForms

    // वर्तमान — नि. ९–१०; विकल्प ए-रूप पाठ ८९
    out.vartaman = table({
        'uttam.eka': [F(aa + 'मि', 'core', 'नि.१०: मि से पहले अ → आ'), F(e + 'मि', 'alt', 'पाठ ८९')],
        'uttam.bahu': [F(aa + 'मो', 'core', 'नि.१०'), F(e + 'मो', 'alt', 'पाठ ८९')],
        'madhyam.eka': [F(s + 'सि', 'core', 'नि.९'), F(e + 'सि', 'alt', 'पाठ ८९')],
        'madhyam.bahu': [F(i + 'त्था', 'core', 'नि.९: -इत्था'), F(e + 'त्था', 'alt', 'पाठ ८९')],
        'anya.eka': [F(s + 'इ', 'core', 'नि.९'), F(e + 'इ', 'alt', 'पाठ ८९')],
        'anya.bahu': [F(s + 'न्ति', 'core', 'नि.९'), F(e + 'ंति', 'alt', 'पाठ ८९')]
    })

    // भूत — नि. ११
    out.bhoot = allSame([F(ii + 'अ', 'core', 'नि.११: सभी पुरुष-वचन में -ईअ')])

    // भविष्यत् — नि. १३, विकल्प -स्स- (पाठ ८९)
    out.bhavishya = table({
        'uttam.eka': [F(i + 'हिमि', 'core', 'नि.१३'), F(i + 'स्सामि', 'alt', 'पाठ ८९')],
        'uttam.bahu': [F(i + 'हामो', 'core', 'नि.१३'), F(i + 'स्सामो', 'alt', 'पाठ ८९')],
        'madhyam.eka': [F(i + 'हिसि', 'core', 'नि.१३'), F(i + 'स्ससि', 'alt', 'पाठ ८९')],
        'madhyam.bahu': [F(i + 'हित्था', 'core', 'नि.१३'), F(i + 'स्सह', 'alt', 'पाठ ८९')],
        'anya.eka': [F(i + 'हिइ', 'core', 'नि.१३'), F(i + 'स्सइ', 'alt', 'पाठ ८९')],
        'anya.bahu': [F(i + 'हिंति', 'core', 'नि.१३'), F(i + 'स्संति', 'alt', 'पाठ ८९')]
    })

    // विधि एवं आज्ञा — नि. १४, विकल्प पाठ ८९
    out.vidhi = table({
        'uttam.eka': [F(s + 'मु', 'core', 'नि.१४')],
        'uttam.bahu': [F(s + 'मो', 'core', 'नि.१४')],
        'madhyam.eka': [F(s + 'हि', 'core', 'नि.१४'), F(s, 'alt', 'पाठ ८९'), F(s + 'सु', 'alt', 'पाठ ८९')],
        'madhyam.bahu': [F(s + 'ह', 'core', 'नि.१४')],
        'anya.eka': [F(s + 'उ', 'core', 'नि.१४')],
        'anya.bahu': [F(s + 'न्तु', 'core', 'नि.१४')]
    })

    // कर्मवाच्य (वर्तमान) — नि. ८३: -ईअ / -इज्ज, अ → आ नहीं होता
    const ia = ii + 'अ'
    const ijj = i + 'ज्ज'

    out.karmani_vartaman = table({
        'uttam.eka': [F(ia + 'मि', 'core', 'नि.८३'), F(ijj + 'मि', 'core', 'नि.८३')],
        'uttam.bahu': [F(ia + 'मो', 'core', 'नि.८३'), F(ijj + 'मो', 'core', 'नि.८३')],
        'madhyam.eka': [F(ia + 'सि', 'core', 'नि.८३'), F(ijj + 'सि', 'core', 'नि.८३')],
        'madhyam.bahu': [F(ia + 'इत्था', 'core', 'नि.८३'), F(i + 'ज्जित्था', 'core', 'नि.८३')],
        'anya.eka': [F(ia + 'इ', 'core', 'नि.८३'), F(ijj + 'इ', 'core', 'नि.८३')],
        'anya.bahu': [F(ia + 'ंति', 'core', 'नि.८३'), F(ijj + 'ंति', 'core', 'नि.८३')]
    })

    out.karmani_bhoot = allSame([F(ia + 'ईअ', 'core', 'नि.८३'), F(i + 'ज्जीअ', 'core', 'नि.८३')])

    out.karmani_vidhi = table({
        'uttam.eka': [F(ia + 'मु'), F(ijj + 'मु')],
        'uttam.bahu': [F(ia + 'मो'), F(ijj + 'मो')],
        'madhyam.eka': [F(ia + 'हि'), F(ijj + 'हि')],
        'madhyam.bahu': [F(ia + 'ह'), F(ijj + 'ह')],
        'anya.eka': [F(ia + 'उ'), F(ijj + 'उ')],
        'anya.bahu': [F(ia + 'ंतु'), F(ijj + 'ंतु')]
    })

    // प्रेरणार्थक — नि. ९५ (पढ + आव + मि = पढावमि); पाठ ८१ में पढावेमि भी छपा है
    const pr = aa + 'व'    // पढाव
    const pre = aa + 'वे'  // पढावे
    const pri = aa + 'वि'  // पढावि

    out.prerak_vartaman = table({
        'uttam.eka': [F(pr + 'मि', 'core', 'नि.९५'), F(pre + 'मि', 'alt', 'पाठ ८१')],
        'uttam.bahu': [F(pr + 'मो', 'core', 'नि.९५'), F(pre + 'मो', 'alt', 'पाठ ८१')],
        'madhyam.eka': [F(pr + 'सि', 'core', 'नि.९५'), F(pre + 'सि', 'alt', 'पाठ ८१')],
        'madhyam.bahu': [F(pri + 'त्था', 'core', 'नि.९५'), F(pre + 'इत्था', 'alt', 'पाठ ८१')],
        'anya.eka': [F(pr + 'इ', 'core', 'नि.९५'), F(pre + 'इ', 'alt', 'पाठ ८१')],
        'anya.bahu': [F(pr + 'न्ति', 'core', 'नि.९५'), F(pre + 'ंति', 'alt', 'पाठ ८१')]
    })

    out.prerak_bhoot = allSame([F(aa + 'वीअ', 'core', 'नि.९५')])

    out.prerak_bhavishya = table({
        'uttam.eka': [F(pri + 'हिमि')],
        'uttam.bahu': [F(pri + 'हामो')],
        'madhyam.eka': [F(pri + 'हिसि')],
        'madhyam.bahu': [F(pri + 'हित्था')],
        'anya.eka': [F(pri + 'हिइ')],
        'anya.bahu': [F(pri + 'हिंति')]
    })

    out.prerak_vidhi = table({
        'uttam.eka': [F(pr + 'मु')],
        'uttam.bahu': [F(pr + 'मो')],
        'madhyam.eka': [F(pr + 'हि')],
        'madhyam.bahu': [F(pr + 'ह')],
        'anya.eka': [F(pr + 'उ')],
        'anya.bahu': [F(pr + 'न्तु')]
    })

    // क्रियातिपत्ति — नि. १०१–१०२: सभी पुरुष-वचन में एक रूप
    out.kriyatipatti = allSame([
        F(e + 'ज्ज', 'core', 'नि.१०२'),
        F(e + 'ज्जा', 'core', 'नि.१०२'),
        F(s + 'न्तो', 'core', 'नि.१०२'),
        F(s + 'माणो', 'core', 'नि.१०२')
    ])

    // कृदन्त — नि. १५–१९, ७३–७९
    const kridanta: KridantaTable = {
        sambandh: [F(i + 'ऊण', 'core', 'नि.१६: -ऊण'), F(i + 'उं', 'alt', 'पाठ ८९'), F(i + 'त्ता', 'alt', 'पाठ ८९'), F(i + 'अ', 'alt', 'पाठ ८९')],
        hetvarth: [F(i + 'उं', 'core', 'नि.१९: -उं')],
        vartaman: [F(s + 'न्त', 'core', 'नि.७३'), F(s + 'माण', 'core', 'नि.७३')],
        bhoot: [F(i + 'अ', 'core', 'नि.७५')],
        bhavishya: [F(i + 'स्संत', 'core', 'नि.७७')],
        vidhi: [F(s + 'णीअ', 'core', 'नि.७७: -अणीअ'), F(e + 'अव्व', 'core', 'नि.७७: -अव्व')],
        karmani_vartaman: [F(ia + 'ंत', 'core', 'नि.८८'), F(ia + 'माण', 'core', 'नि.८८')],
        karmani_bhavishya: [F(i + 'स्समाण', 'core', 'नि.९१')],
        prerak_sambandh: [F(pri + 'ऊण', 'core', 'नि.९७')],
        prerak_hetvarth: [F(pri + 'उं', 'core', 'नि.९७')],
        prerak_vidhi: [F(pr + 'णीअ', 'core', 'नि.९७'), F(pre + 'अव्व', 'core', 'नि.९७')],
        prerak_vartaman: [F(pr + 'ंत', 'core', 'नि.९७'), F(pr + 'माण', 'core', 'नि.९७')],
        prerak_bhoot: [F(pri + 'अ', 'core', 'नि.९७')]
    }

    out.kridanta = kridanta

    return out
}

/** आ / ए / ओ-ending roots: दा, णे, हो … */
export const conjLong = (s: string): VerbForms => {
    const out = {} as VerbForms

    out.vartaman = table({
        'uttam.eka': [F(s + 'मि', 'core', 'नि.१०')],
        'uttam.bahu': [F(s + 'मो', 'core', 'नि.१०')],
        'madhyam.eka': [F(s + 'सि')],
        'madhyam.bahu': [F(s + 'इत्था')],
        'anya.eka': [F(s + 'इ')],
        'anya.bahu': [F(s + 'न्ति')]
    })

    out.bhoot = allSame([F(s + 'ही', 'core', 'नि.१२'), F(s + 'सी', 'alt', 'पाठ ८९'), F(s + 'हीअ', 'alt', 'पाठ ८९')])

    out.bhavishya = table({
        'uttam.eka': [F(s + 'हिमि', 'core', 'नि.१३'), F(s + 'स्सामि', 'alt', 'पाठ ८९')],
        'uttam.bahu': [F(s + 'हामो', 'core', 'नि.१३'), F(s + 'स्सामो', 'alt', 'पाठ ८९')],
        'madhyam.eka': [F(s + 'हिसि'), F(s + 'स्ससि', 'alt', 'पाठ ८९')],
        'madhyam.bahu': [F(s + 'हित्था'), F(s + 'स्सह', 'alt', 'पाठ ८९')],
        'anya.eka': [F(s + 'हिइ'), F(s + 'स्सइ', 'alt', 'पाठ ८९')],
        'anya.bahu': [F(s + 'हिंति'), F(s + 'स्संति', 'alt', 'पाठ ८९')]
    })

    out.vidhi = table({
        'uttam.eka': [F(s + 'मु', 'core', 'नि.१४')],
        'uttam.bahu': [F(s + 'मो')],
        'madhyam.eka': [F(s + 'हि')],
        'madhyam.bahu': [F(s + 'ह')],
        'anya.eka': [F(s + 'उ')],
        'anya.bahu': [F(s + 'न्तु')]
    })

    out.kriyatipatti = allSame([F(s + 'ज्ज', 'core', 'नि.१०२'), F(s + 'ज्जा'), F(s + 'न्तो'), F(s + 'माणो')])

    out.kridanta = {
        sambandh: [F(s + 'ऊण', 'core', 'नि.१७')],
        hetvarth: [F(s + 'उं', 'core', 'नि.१९')]
    }

    if (s === 'दा') {
        out.vidhi.madhyam.eka.push(F('देहि', 'alt', 'पाठ ८९'))

        const alts: [Person, Num, string][] = [
            ['uttam', 'eka', 'देमि'],
            ['uttam', 'bahu', 'देमो'],
            ['madhyam', 'eka', 'देसि'],
            ['madhyam', 'bahu', 'देइत्था'],
            ['anya', 'eka', 'देइ'],
            ['anya', 'bahu', 'देंति']
        ]

        for (const [p, n, f] of alts) out.vartaman[p][n].push(F(f, 'alt', 'पाठ ८९'))
    }

    return out
}

// अस धातु (होना) — पाठ १४
export const AS_DHATU: AsDhatu = {
    root: 'अस',
    hi: 'होना',
    en: 'to be',
    vartaman: table({
        'uttam.eka': [F('अम्हि')],
        'uttam.bahu': [F('म्हो')],
        'madhyam.eka': [F('असि')],
        'madhyam.bahu': [F('थ')],
        'anya.eka': [F('अत्थि')],
        'anya.bahu': [F('संति')]
    }),
    bhoot: allSame([F('अहेसि'), F('आसि')]),
    note: 'निषेध: ण + अत्थि = णत्थि। भूत के दोनों रूप सभी पुरुष-वचन में।'
}
